import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Paper,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import axios from 'axios';

// Import social icons
import { 
  XIcon,
  TelegramIcon,
  DiscordIcon,
  InstagramIcon,
  GitHubIcon,
  LinkedInIcon,
  WebIcon 
} from '../components/icons/SocialIcons';
import { ProfileSkeleton } from '../components/skeletons';

interface PublicProfileData {
  _id: string;
  username: string;
  profilePicture: string;
  bannerImage: string;
  bio: string;
  userRole: string;
  email?: string;
  socialLinks: {
    twitter?: string;
    telegram?: string;
    discord?: string;
    instagram?: string;
    github?: string;
    linkedin?: string;
  };
}

const getSocialIcon = (platform: string, color: string) => {
  switch (platform) {
    case 'twitter': 
      return <XIcon style={{ color }} />;
    case 'telegram': 
      return <TelegramIcon style={{ color: '#229ED9' }} />;
    case 'discord': 
      return <DiscordIcon style={{ color: '#5865F2' }} />;
    case 'instagram': 
      return <InstagramIcon style={{ color: '#E4405F' }} />;
    case 'github': 
      return <GitHubIcon style={{ color }} />;
    case 'linkedin': 
      return <LinkedInIcon style={{ color: '#0A66C2' }} />;
    default: 
      return <WebIcon style={{ color }} />;
  }
};

const PublicProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { mode } = useCustomTheme();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lightGreen = '#0F3B2E';
  
  const isOwnProfile = useMemo(() => 
    currentUser?.username === username, 
    [currentUser?.username, username]
  );
  
  // Colors for the theme
  const colors = useMemo(() => ({
    pageBg: isDark ? '#000000' : '#FAFAFA',
    cardBg: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.6)',
    textPrimary: isDark ? '#FFFFFF' : '#051814',
    textSecondary: isDark ? alpha('#FFFFFF', 0.6) : alpha('#051814', 0.6),
    accent: goldColor,
    success: '#23A559',
    iconColor: isDark ? goldColor : lightGreen,
    borderColor: isDark ? alpha(goldColor, 0.2) : alpha(deepGreen, 0.1),
  }), [isDark]);

  const glassStyle = {
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderColor}`,
    boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.05)',
  };

  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)';

  // Fetch profile data
  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`/api/user/public/${username}`);
      
      if (response.data.success) {
        setProfile(response.data.user);
      } else {
        setError('Profile not found');
      }
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      fetchProfile();
    }
  }, [username]);

  if (loading) {
    return <ProfileSkeleton tabs={3} />;
  }

  if (error || !profile) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error || 'Profile not found'}
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate(-1)}
          sx={{ 
            mt: 2, 
            background: lokiGradient,
            color: goldColor,
            fontWeight: 700,
            borderRadius: '20px',
            px: 4,
            py: 1.5,
            boxShadow: `0 4px 20px ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
            '&:hover': {
              boxShadow: `0 8px 30px ${alpha(isDark ? goldColor : deepGreen, 0.3)}`,
            }
          }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const displayProfilePicture = profile.profilePicture;
  const displayBannerImage = profile.bannerImage;

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: colors.pageBg,
    }}>
      {/* Profile Banner - FULL WIDTH like CommunityDetailPage */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        {displayBannerImage ? (
          <Box
            component="img"
            src={displayBannerImage}
            alt={profile.username}
            sx={{
              width: '100%',
              height: { xs: 200, sm: 250, md: 300 },
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: { xs: 200, sm: 250, md: 300 },
              bgcolor: isDark 
                ? `linear-gradient(135deg, ${alpha(goldColor, 0.2)} 0%, ${alpha(deepGreen, 0.5)} 100%)`
                : `linear-gradient(135deg, ${alpha(goldColor, 0.3)} 0%, ${alpha('#1D5C4A', 0.3)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '4rem' },
                color: isDark ? alpha('#fff', 0.8) : alpha(deepGreen, 0.8),
                textShadow: isDark 
                  ? `0 4px 8px ${alpha('#000', 0.3)}`
                  : `0 4px 8px ${alpha('#fff', 0.3)}`,
                textAlign: 'center',
                px: 2
              }}
            >
              {profile.username}
            </Typography>
          </Box>
        )}
        
        {/* Back Button */}
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            position: 'absolute',
            top: { xs: 16, md: 24 },
            left: { xs: 16, md: 24 },
            bgcolor: alpha('#000', 0.5),
            color: '#fff',
            '&:hover': {
              bgcolor: alpha('#000', 0.7)
            },
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 }
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: '1.5rem', md: '1.8rem' } }} />
        </IconButton>
      </Box>

      {/* Main Content Area - UPDATED to match previous container width */}
      <Container maxWidth="xl" sx={{ 
        py: { xs: 3, md: 4 }, 
        px: { xs: 2, sm: 3, md: 4 }, 
        mt: { xs: -10, sm: -14, md: -16 },
        position: 'relative',
        zIndex: 10
      }}>
        <Box sx={{ 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center' // Center the content box
        }}>
          {/* UPDATED: Added this inner Box with maxWidth to match previous design */}
          <Box sx={{ 
            width: '100%', 
            maxWidth: { md: 800, lg: 1000 } // Same maxWidth as before
          }}>
            
            {/* Profile Header Section */}
            <Box sx={{ 
              mb: 4,
              position: 'relative',
            }}>
              {/* Profile Picture - BIGGER and NO BORDER */}
              <Box sx={{ 
                position: 'relative',
                width: { xs: 120, sm: 140, md: 160 },
                height: { xs: 120, sm: 140, md: 160 },
                borderRadius: '50%',
                overflow: 'visible',
                mb: 2,
                mx: 'auto',
                zIndex: 20
              }}>
                <Box
                  component="img"
                  src={displayProfilePicture || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.username)}&background=${isDark ? 'C4B66F' : '051814'}&color=fff&size=256`}
                  alt={profile.username}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  }}
                  onError={(e) => {
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.username)}&background=${isDark ? 'C4B66F' : '051814'}&color=fff&size=256`;
                  }}
                />
              </Box>

              {/* Username and Role */}
              <Box sx={{ 
                textAlign: 'center',
                mb: 3
              }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{ 
                  fontWeight: 800, 
                  color: colors.textPrimary, 
                  lineHeight: 1.2,
                  mb: 1,
                  wordBreak: 'break-word'
                }}>
                  {profile.username}
                </Typography>
                
                {profile.userRole && (
                  <Typography variant={isMobile ? "subtitle1" : "h6"} color={colors.accent} sx={{ 
                    fontWeight: 600,
                    mb: 1
                  }}>
                    {profile.userRole}
                  </Typography>
                )}
                
                {/* Settings Button - Only for owner */}
                {isOwnProfile && (
                  <Button 
                    variant="contained" 
                    onClick={() => navigate('/profile')}
                    sx={{ 
                      mt: 2,
                      background: lokiGradient,
                      color: goldColor, 
                      textTransform: 'none', 
                      fontWeight: 700,
                      borderRadius: '20px',
                      px: 4,
                      py: 1.5,
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      border: `1px solid ${alpha(goldColor, 0.2)}`,
                      backdropFilter: 'blur(10px)',
                      backgroundColor: alpha(isDark ? deepGreen : '#fff', 0.8),
                    }}
                  >
                    Settings
                  </Button>
                )}
              </Box>
            </Box>

            {/* Bio Section - Now limited to maxWidth */}
            {profile.bio && (
              <Paper elevation={0} sx={{ 
                bgcolor: colors.cardBg, 
                borderRadius: isMobile ? '16px' : '24px', 
                p: isMobile ? 3 : 4, 
                mb: 4, 
                ...glassStyle 
              }}>
                <Typography variant="h6" fontWeight={700} color={colors.textPrimary} sx={{ 
                  mb: 2,
                  fontSize: isMobile ? '1.1rem' : '1.25rem'
                }}>
                  About
                </Typography>
                <Typography variant="body1" color={colors.textSecondary} sx={{ 
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  fontSize: isMobile ? '0.95rem' : '1.05rem'
                }}>
                  {profile.bio}
                </Typography>
              </Paper>
            )}

            {/* Social Links Section - Now limited to maxWidth */}
            <Paper elevation={0} sx={{ 
              bgcolor: colors.cardBg, 
              borderRadius: isMobile ? '16px' : '24px', 
              p: isMobile ? 3 : 4, 
              mb: 4, 
              ...glassStyle 
            }}>
              <Typography variant="h6" fontWeight={700} color={colors.textPrimary} sx={{ 
                mb: 3,
                fontSize: isMobile ? '1.1rem' : '1.25rem'
              }}>
                Social Links
              </Typography>
              
              {Object.values(profile.socialLinks || {}).some(url => url && url.trim() !== '') ? (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  flexWrap: 'wrap', 
                  justifyContent: { xs: 'center', sm: 'flex-start' } 
                }}>
                  {Object.entries(profile.socialLinks || {}).map(([platform, url]) => {
                    if (!url || typeof url !== 'string' || url.trim() === '') return null;
                    const finalUrl = url.includes('://') ? url : platform === 'discord' ? '#' : `https://${platform}.com/${url}`;
                    return (
                      <IconButton
                        key={platform}
                        component="a"
                        href={finalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: isDark ? 'rgba(0,0,0,0.2)' : '#fff',
                          border: `1px solid ${alpha(colors.textSecondary, 0.2)}`,
                          '&:hover': { 
                            bgcolor: alpha(colors.accent, 0.1), 
                            borderColor: colors.accent,
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.2s ease',
                          width: { xs: 52, sm: 56, md: 60 },
                          height: { xs: 52, sm: 56, md: 60 },
                          fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' }
                        }}
                      >
                        {getSocialIcon(platform, colors.iconColor)}
                      </IconButton>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color={colors.textSecondary} sx={{ 
                  textAlign: 'center', 
                  width: '100%',
                  py: 2
                }}>
                  No social links added.
                </Typography>
              )}
            </Paper>

            {/* Profile Details Section - Now limited to maxWidth */}
            <Paper elevation={0} sx={{ 
              bgcolor: colors.cardBg, 
              borderRadius: isMobile ? '16px' : '24px', 
              p: isMobile ? 3 : 4, 
              ...glassStyle 
            }}>
              <Typography variant="h6" fontWeight={700} color={colors.textPrimary} sx={{ 
                mb: 3,
                fontSize: isMobile ? '1.1rem' : '1.25rem'
              }}>
                Profile Details
              </Typography>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                gap: 3 
              }}>
                {/* Email Section */}
                <Box>
                  <Typography variant="caption" fontWeight={700} color={colors.textSecondary} sx={{ 
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1,
                    fontSize: isMobile ? '0.75rem' : '0.85rem'
                  }}>
                    Email
                  </Typography>
                  <Typography variant="body1" color={colors.textPrimary} sx={{ 
                    fontSize: isMobile ? '0.95rem' : '1.05rem'
                  }}>
                    {isOwnProfile && profile.email ? profile.email : 'Email hidden'}
                  </Typography>
                </Box>

                {/* Role Section */}
                <Box>
                  <Typography variant="caption" fontWeight={700} color={colors.textSecondary} sx={{ 
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1,
                    fontSize: isMobile ? '0.75rem' : '0.85rem'
                  }}>
                    Role
                  </Typography>
                  <Typography variant="body1" color={colors.textPrimary} sx={{ 
                    fontSize: isMobile ? '0.95rem' : '1.05rem'
                  }}>
                    {profile.userRole || 'No role set'}
                  </Typography>
                </Box>
              </Box>

              {/* Display Name Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" fontWeight={700} color={colors.textSecondary} sx={{ 
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1,
                  fontSize: isMobile ? '0.75rem' : '0.85rem'
                }}>
                  Display Name
                </Typography>
                <Typography variant="body1" color={colors.textPrimary} sx={{ 
                  fontSize: isMobile ? '0.95rem' : '1.05rem'
                }}>
                  {profile.username}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicProfilePage;